import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { styled, useTheme } from "@mui/material/styles";
import React, { useContext, useEffect, useRef, useState } from "react";
import { BarraContext } from "../../layout/BarraContext";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import ConversationPanel from "../../components/InternalChat/ConversationPanel";
import { getImageUrl } from '../../helpers/imageHelper';
import Ticket from "../../components/Ticket/";
import TicketsManager from "../../components/TicketsManager/";
import toastError from "../../errors/toastError";
import api from "../../services/api";

const ChatContainer = styled('div', { shouldForwardProp: (p) => p !== 'semBarra' })(({ theme, semBarra }) => ({
  flex: 1,
  height: semBarra ? `calc(100% - 20px)` : `calc(100% - 36px)`,
  overflowY: "hidden",
  margin: theme.spacing(0.5, 1, 1, 1), // Jds 02/11: menos espaço morto embaixo da barra
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3],
}));

const ChatPapper = styled('div')(({ theme }) => ({
  display: "flex",
  height: "100%",
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
}));

const WelcomeMsg = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  height: "100%",
  textAlign: "center",
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  boxShadow: "none",
  gap: theme.spacing(3),
}));

const Chat = () => {
  const { t } = useTranslation();
  const { ticketId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  // Conversa interna aberta pela aba "Chat interno" ocupa o mesmo painel do
  // ticket — mas com faixa e cor próprias, para não haver dúvida de destinatário.
  const internalChatId = searchParams.get("interno");
  const [logo, setLogo] = useState(null);
  const themeStorage = localStorage.getItem("theme") || "light";

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const { data } = await api.get("/personalizations");
        if (data && data.length > 0) {
          const lightConfig = data.find(themeConfig => themeConfig.theme === "light");
          const darkConfig = data.find(themeConfig => themeConfig.theme === "dark");

          // Escolhe o logo do tema ativo; se aquele tema nao tiver logo
          // cadastrado, cai no do outro em vez de deixar a tela vazia.
          const doTemaAtivo =
            themeStorage === "dark" ? darkConfig?.logoTicket : lightConfig?.logoTicket;
          setLogo(
            doTemaAtivo || lightConfig?.logoTicket || darkConfig?.logoTicket || null
          );
        }
      } catch (err) {
        toastError(err);
      }
    };

    fetchLogo();
  }, [themeStorage]);

  const theme = useTheme();
  const semBarraCtx = useContext(BarraContext);

  // Divisória ajustável entre a lista de tickets e a conversa (pedido do Jds,
  // 02/11/2026): arrastar muda a largura; duplo clique volta ao padrão. A
  // largura fica guardada por navegador. Só em tela média/grande (md+).
  const telaLarga = useMediaQuery(theme.breakpoints.up("md"));
  const PADRAO = 33.333;
  const lerLargura = () => {
    try {
      const v = parseFloat(window.localStorage.getItem("sz_largura_lista"));
      return v >= 20 && v <= 65 ? v : PADRAO;
    } catch (e) {
      return PADRAO;
    }
  };
  const [largura, setLargura] = useState(lerLargura);
  const [arrastando, setArrastando] = useState(false);
  const containerRef = useRef(null);
  const salvarLargura = v => {
    try {
      window.localStorage.setItem("sz_largura_lista", String(v));
    } catch (e) { /* sem storage: só não lembra */ }
  };
  const iniciarArraste = e => {
    e.preventDefault();
    const rect = containerRef.current && containerRef.current.getBoundingClientRect();
    if (!rect) return;
    setArrastando(true);
    let atual = largura;
    const mover = ev => {
      const pct = ((ev.clientX - rect.left) / rect.width) * 100;
      atual = Math.min(65, Math.max(20, pct));
      setLargura(atual);
    };
    const soltar = () => {
      window.removeEventListener("pointermove", mover);
      window.removeEventListener("pointerup", soltar);
      setArrastando(false);
      salvarLargura(atual);
    };
    window.addEventListener("pointermove", mover);
    window.addEventListener("pointerup", soltar);
  };
  // Mesmo breakpoint (md) do md={4}/md={8} do Grid, senão a regra do MUI vence.
  const larguraEsq = { flexBasis: { md: `${largura}%` }, maxWidth: { md: `${largura}%` } };
  const larguraDir = { flexBasis: { md: `${100 - largura}%` }, maxWidth: { md: `${100 - largura}%` } };

  return (
    <ChatContainer semBarra={semBarraCtx.semBarra}>
      <ChatPapper>
        <Grid container spacing={0} ref={containerRef} sx={{ userSelect: arrastando ? "none" : undefined }}>
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              display: "flex",
              height: "100%",
              flexDirection: "column",
              overflowY: "hidden",
              borderRight: `1px solid ${theme.palette.divider}`,
              position: "relative",
              ...larguraEsq,
              ...((ticketId || internalChatId) && {
                display: { xs: "none", sm: "flex" }
              })
            }}
          >
            {semBarraCtx.barra}
            <TicketsManager />
            {telaLarga && (
              <div
                onPointerDown={iniciarArraste}
                onDoubleClick={() => { setLargura(PADRAO); salvarLargura(PADRAO); }}
                title="Arraste para ajustar a largura (duplo clique volta ao padrão)"
                style={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: 6,
                  height: "100%",
                  cursor: "col-resize",
                  zIndex: 5,
                  background: arrastando ? theme.palette.primary.main : "transparent",
                  opacity: arrastando ? 0.5 : 1
                }}
              />
            )}
          </Grid>
          <Grid 
            item 
            xs={12} 
            md={8} 
            sx={{ 
              display: "flex", 
              height: "100%", 
              flexDirection: "column",
              backgroundColor: theme.palette.background.paper,
              ...larguraDir,
            }}>
            {internalChatId ? (
              <ConversationPanel
                embedded
                chatId={internalChatId}
                onBack={() => setSearchParams({})}
              />
            ) : ticketId ? (
              <>
                <Ticket />
              </>
            ) : (
              <WelcomeMsg sx={{ display: { xs: 'none', sm: 'none', md: 'flex' } }}>
                {logo && (
                  <img 
                    src={getImageUrl(logo)} 
                    style={{ 
                      maxWidth: "60%", 
                      maxHeight: "180px", 
                      objectFit: "contain",
                      marginBottom: "16px",
                      filter: theme => theme.palette.mode === 'dark' ? 'drop-shadow(0 0 8px rgba(255,255,255,0.2))' : 'drop-shadow(0 0 8px rgba(0,0,0,0.1))'
                    }} 
                    alt="logo" 
                  />
                )}
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 500, 
                    color: theme => theme.palette.text.secondary,
                    maxWidth: "80%",
                    lineHeight: 1.5
                  }}
                >
                  {t("chat.noTicketMessage")}
                </Typography>
              </WelcomeMsg>
            )}
          </Grid>
        </Grid>
      </ChatPapper>
    </ChatContainer>
  );
};

export default Chat;
